import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';

/**
 * Une integration, presentee comme toutes les autres.
 *
 * 🚨 Kevin, 2026-09-30 : la page melangeait deux presentations. Les boites
 * mail occupaient une carte a elles seules, avec deux gros boutons pleine
 * largeur, tandis que le CRM tenait dans une ligne a logo, nom, etat et
 * action. Une integration se lit maintenant de la meme facon quelle qu'elle
 * soit : sa marque, ce qu'elle fait, son etat, un geste.
 */

export type IntegrationState = 'connected' | 'disconnected' | 'unavailable' | 'checking';

export interface IntegrationRowProps {
  /** La marque du service, jamais une icone generique. */
  logo: ReactNode;
  name: string;
  /** Ce que l'integration fait, en une ligne. */
  description?: string;
  state: IntegrationState;
  stateLabel: string;
  /** Le seul geste possible sur cette ligne. */
  action?: ReactNode;
  last?: boolean;
}

const TONS: Record<IntegrationState, string> = {
  connected: 'bg-green-100 text-green-700',
  disconnected: 'bg-muted text-muted-foreground',
  unavailable: 'bg-amber-100 text-amber-800',
  checking: 'bg-muted text-muted-foreground',
};

export function IntegrationRow({ logo, name, description, state, stateLabel, action, last }: IntegrationRowProps) {
  return (
    <div className={`px-5 py-4 flex items-center gap-3 ${last ? '' : 'border-b border-border'}`}>
      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">{logo}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{name}</p>
        {/* Wraps: it may carry the reason the integration is unavailable. */}
        {description ? <p className="text-xs text-muted-foreground mt-0.5">{description}</p> : null}
      </div>
      <Badge className={`text-[10px] shrink-0 ${TONS[state]}`}>{stateLabel}</Badge>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
